import {
  ParticipantWithCorral,
  CorralLabel,
  CorralRule,
  CorralGroup,
  CorralStats,
  DEFAULT_CORRAL_RULES,
  CorralParticipant,
  CorralStrategy,
  EditableCorral,
} from "../types/corral.types";

// ─── Wizard corral computation ────────────────────────────────────────────────

const CORRAL_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

export const WIZARD_COLORS = [
  "#ef4444", // A – Red   (front / fastest)
  "#f97316", // B – Orange
  "#eab308", // C – Yellow
  "#7c3aed", // D – Purple
  "#2563eb", // E – Blue
  "#16a34a", // F – Green
  "#0d9488", // G – Teal
  "#db2777", // H – Pink
];

const DEFAULT_COLOR = "#9ca3af";

/**
 * Derives a list of EditableCorral groups from participants.
 *
 * fixed_size – chunk BIB-sorted runners into groups of `fixedSize`.
 * time_based – bucket by expected_finish_time if present; falls back to
 *              evenly-sized groups (4 buckets) when time data is absent.
 */
export function computeCorralGroups(
  participants: CorralParticipant[],
  strategy: CorralStrategy,
  fixedSize: number,
): EditableCorral[] {
  const withBib = [...participants]
    .filter((p) => p.bib_number)
    .sort((a, b) => (a.bib_number ?? 0) - (b.bib_number ?? 0));
  const noBib = participants.filter((p) => !p.bib_number);

  if (withBib.length === 0 && noBib.length === 0) return [];

  const groups: EditableCorral[] = [];

  if (
    strategy === "time_based" &&
    withBib.some((p) => p.expected_finish_time !== undefined)
  ) {
    // ── Time-based bucketing ─────────────────────────────────────────
    const TIME_BRACKETS = [
      { name: "A", maxTime: 45 },
      { name: "B", minTime: 45, maxTime: 60 },
      { name: "C", minTime: 60, maxTime: 90 },
      { name: "D", minTime: 90 },
    ] as { name: string; minTime?: number; maxTime?: number }[];

    const timedSorted = [...withBib]
      .filter((p) => p.expected_finish_time !== undefined)
      .sort((a, b) => (a.expected_finish_time ?? 999) - (b.expected_finish_time ?? 999));

    const untimedWithBib = withBib.filter((p) => p.expected_finish_time === undefined);

    TIME_BRACKETS.forEach((bracket, i) => {
      const inBracket = timedSorted.filter((p) => {
        const t = p.expected_finish_time!;
        const above = bracket.minTime === undefined || t >= bracket.minTime;
        const below = bracket.maxTime === undefined || t < bracket.maxTime;
        return above && below;
      });
      if (inBracket.length === 0) return;

      const sorted = [...inBracket].sort(
        (a, b) => (a.bib_number ?? 0) - (b.bib_number ?? 0),
      );
      groups.push({
        key: bracket.name,
        name: bracket.name,
        bib_start: sorted[0].bib_number!,
        bib_end: sorted[sorted.length - 1].bib_number!,
        max_participants: sorted.length + Math.ceil(sorted.length * 0.1),
        color: WIZARD_COLORS[i % WIZARD_COLORS.length],
        is_default: false,
        participants: sorted,
      });
    });

    // Untimed-with-BIB go to default
    noBib.push(...untimedWithBib);
  } else {
    // ── Fixed-size (or time_based fallback when no timing data) ─────
    const chunk =
      strategy === "fixed_size"
        ? fixedSize
        : Math.max(1, Math.ceil(withBib.length / 4));

    for (let i = 0; i < withBib.length; i += chunk) {
      const slice = withBib.slice(i, i + chunk);
      const letterIdx = Math.floor(i / chunk);
      const name = CORRAL_LETTERS[letterIdx] ?? String(letterIdx + 1);
      groups.push({
        key: name,
        name,
        bib_start: slice[0].bib_number!,
        bib_end: slice[slice.length - 1].bib_number!,
        max_participants: chunk + Math.ceil(chunk * 0.1),
        color: WIZARD_COLORS[letterIdx % WIZARD_COLORS.length],
        is_default: false,
        participants: slice,
      });
    }
  }

  // ── Default corral for no-BIB (overflow) ──────────────────────────
  if (noBib.length > 0) {
    groups.push({
      key: "default",
      name: "Default",
      bib_start: 0,
      bib_end: 0,
      max_participants: noBib.length + Math.ceil(noBib.length * 0.2) + 10,
      color: DEFAULT_COLOR,
      is_default: true,
      participants: noBib,
    });
  }

  return groups;
}

export const getCorralForTime = (time: number, rules: CorralRule[] = DEFAULT_CORRAL_RULES): CorralLabel => {
  for (const rule of rules) {
    const aboveMin = rule.minTime === undefined || time >= rule.minTime;
    const belowMax = rule.maxTime === undefined || time < rule.maxTime;
    if (aboveMin && belowMax) return rule.corral;
  }
  return rules[rules.length - 1].corral;
};

export const assignCorralsToParticipants = (
  participants: ParticipantWithCorral[],
  rules: CorralRule[] = DEFAULT_CORRAL_RULES
): ParticipantWithCorral[] =>
  participants.map((p) => ({
    ...p,
    assigned_corral:
      p.expected_finish_time !== undefined
        ? getCorralForTime(p.expected_finish_time, rules)
        : rules[rules.length - 1].corral,
  }));

export const groupByCorral = (
  participants: ParticipantWithCorral[],
  rules: CorralRule[] = DEFAULT_CORRAL_RULES
): CorralGroup[] => {
  const map = new Map<CorralLabel, ParticipantWithCorral[]>();
  rules.forEach((r) => map.set(r.corral, []));

  participants.forEach((p) => {
    if (p.assigned_corral) {
      map.get(p.assigned_corral)?.push(p);
    }
  });

  return rules.map((rule) => ({
    corral: rule.corral,
    participants: map.get(rule.corral) ?? [],
    rule,
  }));
};

export const computeCorralStats = (participants: ParticipantWithCorral[]): CorralStats => {
  const assigned = participants.filter((p) => p.assigned_corral);
  const byCorral = new Map<CorralLabel, number>();
  assigned.forEach((p) => {
    byCorral.set(p.assigned_corral!, (byCorral.get(p.assigned_corral!) ?? 0) + 1);
  });

  return {
    total: participants.length,
    assigned: assigned.length,
    unassigned: participants.length - assigned.length,
    byCorral: Array.from(byCorral.entries()).map(([corral, count]) => ({ corral, count })),
  };
};
